import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'reactstrap';
import { DYED_UNDYED_DEBOUNCE_MS } from '../../../../../../../../shared/offer-domain/constants';
import { getAlphaImprovementFieldValue } from '../../../../../../../../shared/offer-domain/improvement';

const normalizeValue = value => String(value ?? '').trim();

const AlphaImprovementEditableCells = ({
  detailRow,
  kind = 'offer',
  usesDyedUndyed = false,
  alphaColumns = { dyed: true, undyed: true },
  pricingUnit = null,
  onAlphaImprovementChange,
  rowSpan,
}) => {
  const alphaSuffix = pricingUnit || 'USD/BBL';
  const [dyed, setDyed] = useState('');
  const [undyed, setUndyed] = useState('');
  const [alpha, setAlpha] = useState('');
  const baselineRef = useRef({ dyed: '', undyed: '', alpha: '' });
  const timerRef = useRef(null);

  useEffect(() => {
    const options = { fallbackToBase: false };
    const next = usesDyedUndyed
      ? {
          dyed: getAlphaImprovementFieldValue(detailRow, kind, 'dyed', options),
          undyed: getAlphaImprovementFieldValue(
            detailRow,
            kind,
            'undyed',
            options
          ),
          alpha: '',
        }
      : {
          dyed: '',
          undyed: '',
          alpha: getAlphaImprovementFieldValue(
            detailRow,
            kind,
            'alpha',
            options
          ),
        };
    setDyed(next.dyed);
    setUndyed(next.undyed);
    setAlpha(next.alpha);
    baselineRef.current = next;
  }, [detailRow, kind, usesDyedUndyed]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const emitChange = next => {
    onAlphaImprovementChange?.({
      detailRow,
      kind,
      dyed: usesDyedUndyed ? next.dyed : null,
      undyed: usesDyedUndyed ? next.undyed : null,
      alpha: usesDyedUndyed ? null : next.alpha,
    });
  };

  const scheduleSave = next => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const baseline = baselineRef.current;
      const unchanged = usesDyedUndyed
        ? normalizeValue(next.dyed) === normalizeValue(baseline.dyed) &&
          normalizeValue(next.undyed) === normalizeValue(baseline.undyed)
        : normalizeValue(next.alpha) === normalizeValue(baseline.alpha);
      if (unchanged) return;
      emitChange(next);
      baselineRef.current = {
        dyed: next.dyed ?? '',
        undyed: next.undyed ?? '',
        alpha: next.alpha ?? '',
      };
    }, DYED_UNDYED_DEBOUNCE_MS);
  };

  const cellProps = rowSpan
    ? { rowSpan, className: 'text-dark align-top' }
    : { className: 'text-dark' };

  const renderInput = (value, onValueChange) => (
    <div className='d-flex flex-column gap-1' style={{ minWidth: '72px' }}>
      <Input
        type='number'
        bsSize='sm'
        value={value}
        onChange={e => onValueChange(e.target.value)}
      />
      <span className='text-muted fs-12 text-nowrap' title={alphaSuffix}>
        {alphaSuffix}
      </span>
    </div>
  );

  if (usesDyedUndyed) {
    return (
      <>
        {alphaColumns.dyed && (
          <td {...cellProps}>
            {renderInput(dyed, value => {
              setDyed(value);
              scheduleSave({ dyed: value, undyed, alpha: null });
            })}
          </td>
        )}
        {alphaColumns.undyed && (
          <td {...cellProps}>
            {renderInput(undyed, value => {
              setUndyed(value);
              scheduleSave({ dyed, undyed: value, alpha: null });
            })}
          </td>
        )}
      </>
    );
  }

  return (
    <td {...cellProps}>
      {renderInput(alpha, value => {
        setAlpha(value);
        scheduleSave({ dyed: null, undyed: null, alpha: value });
      })}
    </td>
  );
};

export default AlphaImprovementEditableCells;
